<script setup>
import { computed, reactive, ref, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import TfPopupShell from './TfPopupShell.vue';
import { usePopupToast } from './usePopupToast';
import { createAssistenteMock, formatNow } from './assistenteMock';
import { delayValido, payloadFollowup } from './crmMap.js';
import {
  apiEstado,
  apiConversas,
  apiPausa,
  apiPausaLote,
  apiDelay,
  apiFollowup,
  apiNotificacao,
  apiPrompt,
  apiTreinadorAdd,
  apiTreinadorDel,
  apiQr,
} from './assistenteApi';

const emit = defineEmits(['close']);

const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');
const firstName = computed(
  () => (currentUser.value?.name || '').split(' ')[0] || 'Administrador'
);

const state = reactive(createAssistenteMock());
const { toast, avisar } = usePopupToast();
const carregando = ref(true);

const normalizarTel = tel => String(tel || '').replace(/\D/g, '');

const sincronizarPausasNasConversas = () => {
  const pausados = new Set(
    (state.pausadasExtras || []).map(p => normalizarTel(p.telefone)).filter(Boolean)
  );
  for (const c of state.conversas || []) {
    const tel = normalizarTel(c.telefone);
    if (pausados.has(tel)) c.pausada = true;
  }
};

const aplicarEstado = data => {
  if (!data) return;
  Object.assign(state.whatsapp, data.whatsapp || {});
  Object.assign(state.ia, data.ia || {});
  state.pausadasExtras = (data.pausadas || []).map(p => ({
    ...p,
    telefone: normalizarTel(p.telefone),
  }));
  if (data.delay) Object.assign(state.delay, data.delay);
  if (data.followup) Object.assign(state.followup, data.followup);
  if (data.notificacao) Object.assign(state.notificacao, data.notificacao);
  if (data.treinadores) state.treinadores = data.treinadores;
  if (data.atividade) state.atividade = data.atividade;
  if (typeof data.prompt === 'string') state.prompt = data.prompt;
  sincronizarPausasNasConversas();
};

const hidratarConversas = async () => {
  try {
    const conv = await apiConversas(Number(accountId.value), currentUser.value);
    if (conv?.conversas) {
      state.conversas = conv.conversas.map(c => ({
        ...c,
        telefone: normalizarTel(c.telefone),
      }));
      sincronizarPausasNasConversas();
    }
  } catch (e) {
    /* lista de conversas é secundária — não bloqueia o painel */
  }
};

const recarregar = async ({ nocache = false, conversas = true } = {}) => {
  const acc = Number(accountId.value);
  const est = await apiEstado(acc, currentUser.value, { nocache });
  aplicarEstado(est);
  if (conversas) await hidratarConversas();
};

onMounted(async () => {
  try {
    // 1º paint rápido: estado (WA/IA/prompt). Conversas entram depois.
    await recarregar({ conversas: false });
  } catch (e) {
    avisar(e.message || 'Falha ao carregar o painel');
  } finally {
    carregando.value = false;
  }
  void hidratarConversas();
});

const waChipTexto = computed(() => {
  if (carregando.value || state.whatsapp.conectado == null) return 'Verificando conexão';
  return state.whatsapp.conectado
    ? state.whatsapp.numero || 'Conectado'
    : 'Desconectado';
});
const waChipOn = computed(
  () => !carregando.value && state.whatsapp.conectado === true
);
const iaChipTexto = computed(() => {
  if (carregando.value || state.ia.ativa == null) return 'Verificando…';
  return state.ia.ativa ? 'Ativa' : 'Pausada';
});
const LABEL_VERIFICANDO = 'Verificando conexão';
const statusInstancia = i => {
  const raw = i?.statusLabel || '';
  if (raw === 'Conectando…' || raw === 'Conectando...') return LABEL_VERIFICANDO;
  if (raw) return raw;
  return i?.conectado ? 'Conectado' : 'Offline';
};
const instanciaEmVerificacao = i => {
  const s = statusInstancia(i);
  return s === LABEL_VERIFICANDO || s === 'Verificando…';
};
const badgeClassInstancia = i => {
  if (instanciaEmVerificacao(i)) return 'warn';
  return i?.conectado ? 'on' : 'off';
};

const IA_TRAVADA = true;

const toggleIaGlobal = () => {
  avisar('A IA está desligada de propósito neste momento. O interruptor fica bloqueado.');
};

const qrAberto = ref(false);
const qrImg = ref('');
const qrInstancia = ref('');
let qrTimer = null;

const pararQr = () => {
  clearInterval(qrTimer);
  qrTimer = null;
};

const puxarQr = async () => {
  const inst =
    qrInstancia.value ||
    state.whatsapp.instancias?.find(i => !i.conectado)?.id ||
    state.whatsapp.instancias?.[0]?.id;
  if (!inst) {
    avisar('Nenhuma instância encontrada');
    return;
  }
  qrInstancia.value = inst;
  try {
    const r = await apiQr(Number(accountId.value), currentUser.value, inst);
    if (r.conectado) {
      qrImg.value = '';
      avisar('Esse canal/WhatsApp já está conectado');
      pararQr();
      await recarregar();
      return;
    }
    const raw = r.qr || '';
    qrImg.value = raw;
    if (r.aviso) avisar(r.aviso);
  } catch (e) {
    avisar(e.message);
  }
};

const conectarWhatsapp = async () => {
  const preferida =
    state.whatsapp.instancias?.find(i => !i.conectado)?.id ||
    state.whatsapp.instancias?.[0]?.id ||
    '';
  qrInstancia.value = preferida;
  qrAberto.value = true;
  qrImg.value = '';
  await puxarQr();
  pararQr();
  qrTimer = setInterval(puxarQr, 8000);
};

const fecharQr = () => {
  qrAberto.value = false;
  pararQr();
};

onBeforeUnmount(pararQr);

const salvarPrompt = async () => {
  const texto = String(state.prompt || '').trim();
  if (!texto) {
    avisar('O prompt não pode ficar vazio');
    return;
  }
  try {
    await apiPrompt(Number(accountId.value), currentUser.value, texto);
    avisar('Prompt salvo na produção');
    await recarregar();
  } catch (e) {
    avisar(e.message);
  }
};

const atividadeRef = ref(null);
const verHistorico = () => {
  const el = atividadeRef.value;
  if (!el) return;
  const scroller = el.closest('.overflow-y-auto');
  if (scroller) {
    const top =
      el.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop -
      12;
    scroller.scrollTo({ top, behavior: 'smooth' });
    return;
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const telefone = ref('');
const normalizar = normalizarTel;

const pausadas = computed(() => {
  const byTel = new Map();
  for (const p of state.pausadasExtras || []) {
    const tel = normalizarTel(p.telefone);
    if (tel) byTel.set(tel, { ...p, telefone: tel });
  }
  for (const c of state.conversas || []) {
    if (!c.pausada) continue;
    const tel = normalizarTel(c.telefone);
    if (!tel || byTel.has(tel)) continue;
    byTel.set(tel, {
      telefone: tel,
      motivo: 'Alterado pelo painel',
      quando: c.pausadaEm || c.ultima,
    });
  }
  return [...byTel.values()];
});

const estaPausado = tel =>
  pausadas.value.some(p => p.telefone === normalizarTel(tel));

const marcarPausaLocal = (tels, pausar) => {
  const set = new Set((tels || []).map(normalizarTel).filter(Boolean));
  for (const c of state.conversas || []) {
    if (!set.has(normalizarTel(c.telefone))) continue;
    c.pausada = pausar;
    c.pausadaEm = pausar ? formatNow() : null;
  }
  if (!pausar) {
    state.pausadasExtras = (state.pausadasExtras || []).filter(
      p => !set.has(normalizarTel(p.telefone))
    );
  } else {
    const ja = new Set((state.pausadasExtras || []).map(p => normalizarTel(p.telefone)));
    for (const tel of set) {
      if (ja.has(tel)) continue;
      state.pausadasExtras.unshift({
        telefone: tel,
        motivo: 'Alterado pelo painel 179',
        quando: formatNow(),
      });
      ja.add(tel);
    }
  }
};

const contatoBadge = computed(() => {
  const tel = normalizar(telefone.value);
  if (!tel) return { texto: '—', classe: '' };
  return estaPausado(tel)
    ? { texto: 'IA pausada', classe: 'off' }
    : { texto: 'IA ativa', classe: 'on' };
});

const definirPausa = async (tel, pausar) => {
  const t = normalizarTel(tel);
  await apiPausa(Number(accountId.value), currentUser.value, t, pausar);
  marcarPausaLocal([t], pausar);
  await recarregar({ nocache: true });
};

const toggleContato = async pausar => {
  const tel = normalizar(telefone.value);
  if (tel.length < 10) {
    avisar('Informe um telefone válido');
    return;
  }
  try {
    await definirPausa(tel, pausar);
    avisar(pausar ? `IA desligada em ${tel}` : `IA ligada em ${tel}`);
  } catch (e) {
    avisar(e.message);
  }
};

const religarRapido = async tel => {
  try {
    await definirPausa(tel, false);
    avisar(`IA religada em ${tel}`);
  } catch (e) {
    avisar(e.message);
  }
};

const ultimaSync = ref('');
const sincronizar = async () => {
  try {
    await recarregar({ nocache: true });
    ultimaSync.value = formatNow();
    avisar('Painel atualizado');
  } catch (e) {
    avisar(e.message);
  }
};

const selecionadas = ref([]);
const todasSelecionadas = computed(
  () =>
    selecionadas.value.length > 0 &&
    selecionadas.value.length === state.conversas.length
);
const toggleTodas = marcar => {
  selecionadas.value = marcar ? state.conversas.map(c => c.telefone) : [];
};
const resumoLote = computed(() => {
  const total = state.conversas.length;
  const nPausadas = pausadas.value.length;
  const ativasNaLista = state.conversas.filter(c => !estaPausado(c.telefone)).length;
  return `${total} conversas · ${nPausadas} pausadas · ${ativasNaLista} IA ativa na lista`;
});
const aplicarLote = async pausar => {
  let tels = (selecionadas.value || []).map(normalizarTel).filter(t => t.length >= 10);
  if (!tels.length) {
    avisar('Selecione pelo menos uma conversa');
    return;
  }
  // Religar: se nenhuma selecionada está pausada, usa a lista "Somente pausadas agora".
  if (!pausar) {
    const pausadasSel = tels.filter(t => estaPausado(t));
    if (!pausadasSel.length) {
      const todasPausadas = pausadas.value.map(p => p.telefone);
      if (!todasPausadas.length) {
        avisar('Nenhuma conversa pausada para religar');
        return;
      }
      tels = todasPausadas;
    } else {
      tels = pausadasSel;
    }
  }
  try {
    await apiPausaLote(
      Number(accountId.value),
      currentUser.value,
      tels,
      pausar
    );
    marcarPausaLocal(tels, pausar);
    selecionadas.value = [];
    avisar(
      `${tels.length} conversa(s) ${pausar ? 'pausada(s)' : 'religada(s)'}`
    );
    await recarregar({ nocache: true });
  } catch (e) {
    avisar(e.message);
  }
};

const religarTodasPausadas = async () => {
  const tels = pausadas.value.map(p => p.telefone);
  if (!tels.length) {
    avisar('Nenhuma conversa pausada');
    return;
  }
  selecionadas.value = tels;
  await aplicarLote(false);
};

const salvarDelay = async () => {
  const min = Number(state.delay.min);
  const max = Number(state.delay.max);
  if (!delayValido(min, max)) {
    avisar('Delay inválido: use 0 a 120 s e mínimo ≤ máximo');
    return;
  }
  try {
    await apiDelay(Number(accountId.value), currentUser.value, min, max);
    avisar(`Delay salvo: ${min}–${max} s`);
  } catch (e) {
    avisar(e.message);
  }
};

const abrirFollowups = () =>
  avisar('Follow-ups avançados ainda não têm página própria neste servidor');
const salvarFollowup = async () => {
  try {
    const payload = payloadFollowup(state.followup, IA_TRAVADA);
    await apiFollowup(Number(accountId.value), currentUser.value, payload);
    state.followup.ativo = payload.ativo;
    avisar(`Follow-up ${payload.ativo ? 'ligado' : 'desligado'}`);
  } catch (e) {
    avisar(e.message);
  }
};

const salvarNotificacao = async () => {
  try {
    await apiNotificacao(Number(accountId.value), currentUser.value, state.notificacao);
    avisar(
      `Transferência humana ${state.notificacao.ativa ? 'ativa' : 'desativada'}`
    );
  } catch (e) {
    avisar(e.message);
  }
};
const testarNotificacao = async () => {
  try {
    await apiNotificacao(Number(accountId.value), currentUser.value, {
      ...state.notificacao,
      teste: true,
    });
    avisar('Evento de teste registrado');
    await recarregar();
  } catch (e) {
    avisar(e.message);
  }
};

const novoTreinador = reactive({ telefone: '', nome: '' });
const cadastrarTreinador = async () => {
  const tel = normalizar(novoTreinador.telefone);
  if (tel.length < 10) {
    avisar('Informe um telefone válido');
    return;
  }
  try {
    const r = await apiTreinadorAdd(
      Number(accountId.value),
      currentUser.value,
      tel,
      novoTreinador.nome.trim()
    );
    state.treinadores = r.treinadores;
    novoTreinador.telefone = '';
    novoTreinador.nome = '';
    avisar(`Treinador ${tel} cadastrado`);
  } catch (e) {
    avisar(e.message);
  }
};
const removerTreinador = async tel => {
  try {
    const r = await apiTreinadorDel(Number(accountId.value), currentUser.value, tel);
    state.treinadores = r.treinadores;
    avisar(`Treinador ${tel} removido`);
  } catch (e) {
    avisar(e.message);
  }
};

const periodo = ref(7);
const atividadeFiltrada = computed(() =>
  state.atividade.filter(a => a.minutos <= periodo.value * 1440)
);
const iconeAtividade = {
  pausa: 'i-lucide-pause-circle text-[var(--tf-off-fg)]',
  religada: 'i-lucide-play-circle text-[var(--tf-on-fg)]',
  notif: 'i-lucide-bell-ring text-[var(--tf-warn-fg)]',
};
const variaveis = [
  '#name_contact#',
  '#phone_contact#',
  '#date#',
  '#time#',
  '#motivo#',
];
</script>

<template>
  <TfPopupShell label="Assistente virtual" :toast="toast" @close="emit('close')">
    <div
      v-if="qrAberto"
      class="absolute inset-0 z-30 flex items-center justify-center bg-black/50 p-4"
      @click.self="fecharQr"
    >
      <div class="tf-panel w-full max-w-md">
        <div class="tf-panel-head">
          <div>
            <h2 class="tf-panel-title">Conectar WhatsApp</h2>
            <p class="tf-panel-sub">
              Mesmo WhatsApp oficial do Chatwoot de produção. Escolha a
              instância — Atendimento é o segundo número.
            </p>
          </div>
          <button type="button" class="tf-btn ghost icon sm" @click="fecharQr">
            <span class="i-lucide-x size-4" />
          </button>
        </div>
        <div class="tf-panel-body">
          <div v-if="state.whatsapp.instancias?.length" class="tf-field">
            <label>Instância</label>
            <select v-model="qrInstancia" @change="puxarQr">
              <option
                v-for="i in state.whatsapp.instancias"
                :key="i.id"
                :value="i.id"
              >
                {{ i.label }} — {{ i.conectado ? 'conectado' : 'offline' }}
                <template v-if="i.numero"> ({{ i.numero }})</template>
              </option>
            </select>
          </div>
          <div v-if="qrImg" class="flex justify-center p-3 rounded-xl bg-white">
            <img :src="qrImg" alt="QR Code WhatsApp" class="w-56 h-56" />
          </div>
          <p v-else class="tf-hint text-center">
            {{
              state.whatsapp.instancias?.find(i => i.id === qrInstancia)
                ?.conectado
                ? 'Esta instância já está conectada.'
                : 'Gerando QR do WhatsApp…'
            }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button type="button" class="tf-btn primary" @click="puxarQr">
              Atualizar QR
            </button>
            <button type="button" class="tf-btn ghost" @click="fecharQr">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-1 overflow-y-auto">
      <div class="max-w-[1120px] mx-auto grid gap-4 p-5 pt-4">
        <header class="flex flex-wrap items-start justify-between gap-3 pr-10">
          <div class="min-w-0">
            <h1 class="m-0 text-xl font-bold tracking-tight">
              Olá, {{ firstName }}!
            </h1>
            <p class="m-0 text-sm text-[var(--tf-muted)] truncate">
              {{ currentUser?.email }}
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2" role="status">
            <span class="tf-chip">
              <span
                class="tf-dot"
                :class="
                  waChipOn
                    ? 'on'
                    : carregando || state.whatsapp.conectado == null
                      ? 'warn'
                      : 'off'
                "
              />
              WhatsApp: {{ waChipTexto }}
            </span>
            <span class="tf-chip">
              <span
                class="tf-dot"
                :class="state.ia.ativa === true ? 'on' : state.ia.ativa === false ? 'off' : ''"
              />
              IA: {{ iaChipTexto }}
            </span>
            <button type="button" class="tf-btn secondary" @click="sincronizar">
              Atualizar
            </button>
            <button type="button" class="tf-btn primary" @click="conectarWhatsapp">
              Conectar WhatsApp
            </button>
          </div>
        </header>

        <p v-if="carregando" class="tf-hint">Carregando painel…</p>
        <div
          v-if="!carregando && state.whatsapp.conectado === false"
          class="tf-banner"
        >
          WhatsApp desconectado — a IA fica inativa até o número ser
          conectado.
        </div>

        <section
          class="grid gap-3 md:grid-cols-2"
          aria-label="Controles principais"
        >
          <article class="tf-panel">
            <div class="tf-panel-head">
              <div>
                <h2 class="tf-panel-title">Atendimento pela IA</h2>
                <p class="tf-panel-sub">
                  IA desligada de propósito — o interruptor fica bloqueado
                </p>
              </div>
              <div class="tf-panel-actions">
                <span class="tf-badge" :class="state.ia.ativa ? 'on' : 'off'">
                  {{ state.ia.ativa ? 'IA Ativa' : 'IA Pausada' }}
                </span>
                <button
                  type="button"
                  class="tf-btn secondary sm"
                  @click="verHistorico"
                >
                  Histórico
                </button>
              </div>
            </div>
            <div class="tf-panel-body">
              <div class="flex items-center gap-3.5">
                <label
                  class="tf-switch"
                  :class="{ 'opacity-50 cursor-not-allowed': IA_TRAVADA }"
                  :title="
                    IA_TRAVADA
                      ? 'IA desligada de propósito. Não é possível ligar agora.'
                      : ''
                  "
                >
                  <input
                    type="checkbox"
                    :checked="false"
                    :disabled="IA_TRAVADA"
                    @click.prevent="toggleIaGlobal"
                    @change.prevent="toggleIaGlobal"
                  />
                  <span class="tf-switch-track">
                    <span class="tf-switch-thumb" />
                  </span>
                </label>
                <div>
                  <div class="text-lg font-bold tracking-tight">
                    {{ state.ia.ativa ? 'Ativa' : 'Inativa' }}
                  </div>
                  <div class="text-[13px] text-[var(--tf-muted)]">
                    {{
                      state.ia.ativa
                        ? 'IA respondendo todos os contatos'
                        : 'IA pausada para todos os contatos'
                    }}
                  </div>
                </div>
              </div>
              <dl class="tf-meta">
                <div>
                  <dt>Estado</dt>
                  <dd>
                    {{
                      state.ia.ativa ? 'Ativa para todos' : 'Pausada para todos'
                    }}
                  </dd>
                </div>
                <div>
                  <dt>Última alteração</dt>
                  <dd>{{ state.ia.atualizado }}</dd>
                </div>
              </dl>
            </div>
          </article>

          <article class="tf-panel">
            <div class="tf-panel-head">
              <div>
                <h2 class="tf-panel-title">WhatsApp</h2>
                <p class="tf-panel-sub">
                  Canais WhatsApp — Comercial, Atendimento e Teste
                </p>
              </div>
              <span
                class="tf-badge"
                :class="
                  carregando || state.whatsapp.conectado == null
                    ? 'warn'
                    : state.whatsapp.conectado
                      ? 'on'
                      : 'off'
                "
              >
                {{
                  carregando || state.whatsapp.conectado == null
                    ? 'Verificando conexão'
                    : state.whatsapp.conectado
                      ? 'Conectado'
                      : 'Desconectado'
                }}
              </span>
            </div>
            <div class="tf-panel-body">
              <div class="text-[22px] font-bold tracking-tight">
                {{
                  carregando || state.whatsapp.conectado == null
                    ? 'Verificando conexão'
                    : state.whatsapp.conectado
                      ? 'Conectado'
                      : 'Desconectado'
                }}
              </div>
              <dl class="tf-meta">
                <div>
                  <dt>Conectados</dt>
                  <dd>{{ state.whatsapp.conectados }}</dd>
                </div>
                <div>
                  <dt>Atualizado</dt>
                  <dd>{{ state.whatsapp.atualizado }}</dd>
                </div>
              </dl>
              <div
                v-if="state.whatsapp.instancias?.length"
                class="tf-list max-h-[160px]"
              >
                <div
                  v-for="i in state.whatsapp.instancias"
                  :key="i.id"
                  class="tf-list-item"
                >
                  <div class="min-w-0">
                    <div class="font-semibold">{{ i.label }}</div>
                    <div class="text-xs text-[var(--tf-muted)]">
                      {{ i.numero || 'sem número' }}
                    </div>
                  </div>
                  <span class="tf-badge" :class="badgeClassInstancia(i)">
                    {{ statusInstancia(i) }}
                  </span>
                </div>
              </div>
            </div>
          </article>
        </section>

        <section class="tf-panel" aria-label="Prompt da IA">
          <div class="tf-panel-head">
            <div>
              <h2 class="tf-panel-title">Prompt da IA</h2>
              <p class="tf-panel-sub">
                Mesmas instruções do painel oficial — salva direto na produção
              </p>
            </div>
            <span class="tf-badge">
              {{ (state.prompt || '').length }} caracteres
            </span>
          </div>
          <div class="tf-panel-body">
            <div class="tf-field">
              <label for="av-prompt">Instruções do sistema</label>
              <textarea
                id="av-prompt"
                v-model="state.prompt"
                rows="12"
                class="font-mono text-[13px]"
                placeholder="Carregando prompt da produção…"
              />
            </div>
            <div>
              <button type="button" class="tf-btn primary" @click="salvarPrompt">
                Salvar prompt
              </button>
            </div>
          </div>
        </section>

        <section class="tf-panel" aria-label="Controle por telefone">
          <div class="tf-panel-head">
            <div>
              <h2 class="tf-panel-title">Por telefone</h2>
              <p class="tf-panel-sub">
                Mesmo controle do CRM — liga ou desliga a IA neste número
              </p>
            </div>
            <span class="tf-badge" :class="contatoBadge.classe">
              {{ contatoBadge.texto }}
            </span>
          </div>
          <div class="tf-panel-body">
            <div class="flex flex-wrap items-end gap-3">
              <div class="tf-field flex-1 min-w-[200px]">
                <label for="av-pausa-telefone">Telefone do cliente</label>
                <input
                  id="av-pausa-telefone"
                  v-model="telefone"
                  type="text"
                  placeholder="Ex: 5531999999999"
                />
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="tf-btn secondary"
                  @click="sincronizar"
                >
                  Sincronizar
                </button>
                <button
                  type="button"
                  class="tf-btn danger"
                  @click="toggleContato(true)"
                >
                  Desligar IA
                </button>
                <button
                  type="button"
                  class="tf-btn success"
                  @click="toggleContato(false)"
                >
                  Ligar IA
                </button>
              </div>
            </div>
            <p v-if="ultimaSync" class="tf-hint">
              Última sync {{ ultimaSync }}
            </p>

            <div class="tf-section-label">Conversas iniciadas — lote</div>
            <div class="flex flex-wrap items-center gap-2">
              <label class="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  :checked="todasSelecionadas"
                  @change="toggleTodas($event.target.checked)"
                />
                Todas
              </label>
              <button
                type="button"
                class="tf-btn danger sm"
                @click="aplicarLote(true)"
              >
                Pausar selecionadas
              </button>
              <button
                type="button"
                class="tf-btn success sm"
                @click="aplicarLote(false)"
              >
                Religar selecionadas
              </button>
              <span class="tf-hint">{{ resumoLote }}</span>
            </div>
            <div class="tf-list max-h-[280px]">
              <div
                v-for="c in state.conversas"
                :key="c.telefone"
                class="tf-list-item"
              >
                <label
                  class="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                >
                  <input
                    v-model="selecionadas"
                    type="checkbox"
                    :value="c.telefone"
                  />
                  <div class="min-w-0">
                    <div class="font-semibold tabular-nums">
                      {{ c.telefone }}
                    </div>
                    <div class="text-xs text-[var(--tf-muted)]">
                      {{ c.mensagens }} msgs · última {{ c.ultima }}
                    </div>
                  </div>
                </label>
                <span
                  class="tf-badge"
                  :class="estaPausado(c.telefone) ? 'off' : 'on'"
                >
                  {{ estaPausado(c.telefone) ? 'IA pausada' : 'IA ativa' }}
                </span>
              </div>
            </div>

            <div class="tf-section-label flex flex-wrap items-center gap-2">
              <span>Somente pausadas agora</span>
              <button
                v-if="pausadas.length"
                type="button"
                class="tf-btn success sm"
                @click="religarTodasPausadas"
              >
                Religar todas ({{ pausadas.length }})
              </button>
            </div>
            <div class="tf-list">
              <div v-if="!pausadas.length" class="tf-list-empty">
                Nenhum contato pausado.
              </div>
              <div
                v-for="p in pausadas"
                :key="p.telefone"
                class="tf-list-item"
              >
                <div class="min-w-0">
                  <div class="font-semibold tabular-nums">{{ p.telefone }}</div>
                  <div class="text-xs text-[var(--tf-muted)]">
                    {{ p.motivo }} · {{ p.quando }}
                  </div>
                </div>
                <button
                  type="button"
                  class="tf-btn success sm"
                  @click="religarRapido(p.telefone)"
                >
                  Religar
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="grid gap-3 md:grid-cols-2" aria-label="Ajustes">
          <article class="tf-panel">
            <div class="tf-panel-head">
              <div>
                <h2 class="tf-panel-title">Delay</h2>
                <p class="tf-panel-sub">Segundos antes de cada resposta</p>
              </div>
            </div>
            <div class="tf-panel-body">
              <div class="grid grid-cols-2 gap-3">
                <div class="tf-field">
                  <label for="av-delay-min">Mínimo</label>
                  <input
                    id="av-delay-min"
                    v-model.number="state.delay.min"
                    type="number"
                    min="0"
                    max="120"
                  />
                </div>
                <div class="tf-field">
                  <label for="av-delay-max">Máximo</label>
                  <input
                    id="av-delay-max"
                    v-model.number="state.delay.max"
                    type="number"
                    min="0"
                    max="120"
                  />
                </div>
              </div>
              <div>
                <button
                  type="button"
                  class="tf-btn primary"
                  @click="salvarDelay"
                >
                  Salvar
                </button>
              </div>
            </div>
          </article>

          <article class="tf-panel">
            <div class="tf-panel-head">
              <div>
                <h2 class="tf-panel-title">Follow-up</h2>
                <p class="tf-panel-sub">
                  Fases, tentativas e tempos (página completa)
                </p>
              </div>
              <span
                class="tf-badge"
                :class="state.followup.ativo ? 'on' : 'off'"
              >
                {{ IA_TRAVADA || !state.followup.ativo ? 'Desativado' : 'Ativado' }}
              </span>
            </div>
            <div class="tf-panel-body">
              <p class="tf-hint text-[13px]">
                Com a IA desligada, o follow-up também fica desligado.
              </p>
              <div>
                <button
                  type="button"
                  class="tf-btn primary"
                  @click="abrirFollowups"
                >
                  Abrir Follow-ups →
                </button>
              </div>
              <label
                class="flex items-center gap-3 text-sm"
                :class="IA_TRAVADA ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
              >
                <span class="tf-switch">
                  <input
                    :checked="false"
                    :disabled="IA_TRAVADA"
                    type="checkbox"
                  />
                  <span class="tf-switch-track">
                    <span class="tf-switch-thumb" />
                  </span>
                </span>
                Chave geral (ligado/desligado)
              </label>
              <div class="tf-field">
                <label for="av-fup-minutos">
                  Minutos (legado — preferir a página Follow-ups)
                </label>
                <input
                  id="av-fup-minutos"
                  v-model.number="state.followup.minutos"
                  type="number"
                  min="1"
                  max="1440"
                  class="!max-w-[120px]"
                />
              </div>
              <div class="tf-field">
                <label for="av-fup-instrucoes">Instruções (legado)</label>
                <textarea
                  id="av-fup-instrucoes"
                  v-model="state.followup.instrucoes"
                  rows="2"
                  maxlength="2000"
                  placeholder="Ex: Só faça follow-up se o cliente iniciou um pedido…"
                />
              </div>
              <div>
                <button
                  type="button"
                  class="tf-btn primary"
                  @click="salvarFollowup"
                >
                  Salvar chave rápida
                </button>
              </div>
            </div>
          </article>

          <article class="tf-panel">
            <div class="tf-panel-head">
              <div>
                <h2 class="tf-panel-title">Transferência humana</h2>
                <p class="tf-panel-sub">Notifica e pausa a IA no contato</p>
              </div>
              <div class="tf-panel-actions">
                <span
                  class="tf-badge"
                  :class="state.notificacao.ativa ? 'on' : 'off'"
                >
                  {{ state.notificacao.ativa ? 'Ativa' : 'Inativa' }}
                </span>
                <button
                  type="button"
                  class="tf-btn secondary sm"
                  @click="verHistorico"
                >
                  Histórico
                </button>
              </div>
            </div>
            <div class="tf-panel-body">
              <div class="tf-field">
                <label for="av-notif-telefone">Telefone destino</label>
                <input
                  id="av-notif-telefone"
                  v-model="state.notificacao.telefone"
                  type="text"
                  placeholder="Ex: 5531999999999"
                />
              </div>
              <div class="tf-field">
                <label for="av-notif-mensagem">Mensagem</label>
                <textarea
                  id="av-notif-mensagem"
                  v-model="state.notificacao.mensagem"
                  rows="3"
                />
                <div class="flex flex-wrap gap-1 text-[11px]">
                  <code
                    v-for="v in variaveis"
                    :key="v"
                    class="px-1.5 py-0.5 rounded bg-[var(--tf-hover)]"
                  >
                    {{ v }}
                  </code>
                </div>
              </div>
              <label class="flex items-center gap-2 text-sm cursor-pointer">
                <input v-model="state.notificacao.ativa" type="checkbox" />
                Ativar notificação
              </label>
              <div class="flex gap-2">
                <button
                  type="button"
                  class="tf-btn primary"
                  @click="salvarNotificacao"
                >
                  Salvar
                </button>
                <button
                  type="button"
                  class="tf-btn secondary"
                  @click="testarNotificacao"
                >
                  Teste
                </button>
              </div>
            </div>
          </article>

          <article class="tf-panel">
            <div class="tf-panel-head">
              <div>
                <h2 class="tf-panel-title">Modo treinador</h2>
                <p class="tf-panel-sub">
                  Patches do treinador ficam no painel oficial — aqui o prompt acima já é o da produção
                </p>
              </div>
              <span class="tf-badge on">
                {{ state.treinadores.length }} cadastrado(s)
              </span>
            </div>
            <div class="tf-panel-body">
              <div class="tf-field">
                <label for="av-treinador-telefone">Telefone</label>
                <input
                  id="av-treinador-telefone"
                  v-model="novoTreinador.telefone"
                  type="text"
                  placeholder="Ex: 5531999999999"
                  autocomplete="off"
                />
              </div>
              <div class="tf-field">
                <label for="av-treinador-nome">Nome (opcional)</label>
                <input
                  id="av-treinador-nome"
                  v-model="novoTreinador.nome"
                  type="text"
                  placeholder="Ex: Diogo"
                  autocomplete="off"
                />
              </div>
              <p class="tf-hint text-[11px]">
                Só esses números editam prompt/aprendizados pelo WhatsApp.
                Cliente final não entra aqui.
              </p>
              <div class="flex gap-2">
                <button
                  type="button"
                  class="tf-btn primary"
                  @click="cadastrarTreinador"
                >
                  Cadastrar
                </button>
                <button
                  type="button"
                  class="tf-btn secondary"
                  @click="recarregar().then(() => avisar('Lista recarregada da produção'))"
                >
                  Atualizar
                </button>
              </div>
              <div class="tf-list max-h-[180px]">
                <div v-if="!state.treinadores.length" class="tf-list-empty">
                  Nenhum treinador cadastrado.
                </div>
                <div
                  v-for="t in state.treinadores"
                  :key="t.telefone"
                  class="tf-list-item"
                >
                  <div class="min-w-0">
                    <div class="font-semibold tabular-nums">
                      {{ t.telefone }}
                    </div>
                    <div class="text-xs text-[var(--tf-muted)]">
                      {{ t.nome || 'Sem nome' }}
                    </div>
                  </div>
                  <button
                    type="button"
                    class="tf-btn danger sm"
                    @click="removerTreinador(t.telefone)"
                  >
                    Remover
                  </button>
                </div>
              </div>
            </div>
          </article>
        </section>

        <section
          ref="atividadeRef"
          class="tf-panel"
          aria-label="Atividade recente"
        >
          <div class="tf-panel-head">
            <div>
              <h2 class="tf-panel-title">Atividade recente</h2>
              <p class="tf-panel-sub">Pausas, religadas e notificações</p>
            </div>
            <div class="tf-panel-actions">
              <select
                v-model.number="periodo"
                class="tf-input !h-8 !w-auto !py-0"
              >
                <option :value="7">7 dias</option>
                <option :value="30">30 dias</option>
                <option :value="90">90 dias</option>
              </select>
              <button
                type="button"
                class="tf-btn ghost sm"
                @click="periodo = 90"
              >
                Ver histórico completo
              </button>
            </div>
          </div>
          <div class="tf-panel-body">
            <div class="tf-list">
              <div v-if="!atividadeFiltrada.length" class="tf-list-empty">
                Nenhuma atividade no período.
              </div>
              <div
                v-for="(a, i) in atividadeFiltrada"
                :key="`${a.quando}-${i}`"
                class="tf-list-item !justify-start"
              >
                <span class="size-4 flex-shrink-0" :class="iconeAtividade[a.tipo]" />
                <span class="flex-1 min-w-0 truncate">{{ a.texto }}</span>
                <span class="text-xs text-[var(--tf-muted)] tabular-nums">
                  {{ a.quando }}
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </TfPopupShell>
</template>
